import { createContext, useContext, useEffect, type ReactNode } from "react";
import type { Brandkit } from "@/modules/feedback/types/api.types";
import { getTextColorForBackground } from "@/utils";

export interface ThemeConfig {
	// Brand identity
	logoUrl?: string;

	// Colors
	primaryColor: string; // Main brand color (used for backgrounds, large areas)
	accentColor: string; // Accent/CTA color (used for buttons, links)
	textOnPrimary?: string; // Text color on primary background (auto-calculated if not provided)

	// Typography
	headingFont?: string;
	bodyFont?: string;
	headingFontWeight?: string;
	bodyFontWeight?: string;
}

interface ThemeContextValue {
	theme: ThemeConfig;
}

const defaultTheme: ThemeConfig = {
	primaryColor: "#F4A100", // Warm golden yellow
	accentColor: "#E63946", // Vibrant red
	headingFont: "system-ui, -apple-system, sans-serif",
	bodyFont: "system-ui, -apple-system, sans-serif",
};

const ThemeContext = createContext<ThemeContextValue>({ theme: defaultTheme });

/**
 * Convert API brandkit to ThemeConfig
 */
export function brandkitToTheme(brandkit: Brandkit): ThemeConfig {
	return {
		logoUrl:
			brandkit.logo_icon_with_label_url ||
			brandkit.logo_icon_only_url ||
			undefined,
		primaryColor: brandkit.color_primary_hex,
		accentColor: brandkit.color_accent_hex,
		headingFont: `'${brandkit.font_heading_name}', system-ui, sans-serif`,
		headingFontWeight: `${brandkit.font_heading_weight}`,
		bodyFont: `'${brandkit.font_body_name}', system-ui, sans-serif`,
		bodyFontWeight: `${brandkit.font_body_weight}`,
	};
}

interface ThemeProviderProps {
	theme?: Partial<ThemeConfig>;
	children: ReactNode;
}

export function ThemeProvider({
	theme: userTheme,
	children,
}: ThemeProviderProps) {
	const theme: ThemeConfig = {
		...defaultTheme,
		...userTheme,
	};

	// Auto-calculate text color if not provided
	if (!theme.textOnPrimary) {
		theme.textOnPrimary = getTextColorForBackground(theme.accentColor);
	}

	// Apply CSS custom properties
	useEffect(() => {
		const root = document.documentElement;
		root.style.setProperty("--color-primary", theme.primaryColor);
		root.style.setProperty("--color-accent", theme.accentColor);
		root.style.setProperty("--color-t-primary", "#1f1f1f");
		root.style.setProperty(
			"--color-t-on-primary",
			theme.textOnPrimary || "#1a1a1a",
		);
		root.style.setProperty(
			"--font-heading",
			theme.headingFont || "system-ui",
		);
		root.style.setProperty(
			"--font-heading-weight",
			theme.headingFontWeight || "700",
		);
		root.style.setProperty("--font-body", theme.bodyFont || "system-ui");
		root.style.setProperty(
			"--font-body-weight",
			theme.bodyFontWeight || "400",
		);
	}, [theme]);

	return (
		<ThemeContext.Provider value={{ theme }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}
